import React, { useEffect, useMemo, useState } from "react";
import { styled } from '@mui/material/styles';
import {
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Button,
  Box,
  Paper,
  IconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
const PREFIX = 'CustomAutocomplete';

const classes = {
  textField: `${PREFIX}-textField`
};

const Root = styled('div')(() => ({
  [`& .${classes.textField}`]: {
    "& .MuiInputBase-input": { color: "white" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "white" },
      "&.Mui-focused fieldset": { borderColor: "white" },
    },
    "& .MuiInputLabel-root": { color: "white", "&.Mui-focused": { color: "white" } },
    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": { color: "white" },
  }
}));

// keep falsy values like 0 / ''
const normalizeToArray = (v) => (Array.isArray(v) ? v.slice() : v != null ? [v] : []);

const getLabel = (o) =>
  o == null ? "" : typeof o === "string" ? o : o.label ?? o.value ?? o.name ?? String(o);

const makeIsEqual = (getOptionId) => (option, value) => {
  if (option === value) return true;
  if (getOptionId) {
    const ao = getOptionId(option);
    const av = getOptionId(value);
    return ao !== undefined && av !== undefined && ao === av;
  }
  if (typeof option === "string" && typeof value === "string") return option === value;
  if (option && value && typeof option === "object" && typeof value === "object") {
    const keys = ["value", "id", "key", "code"];
    for (const k of keys) if (option[k] != null && value[k] != null) return option[k] === value[k];
  }
  return false;
};

const makeEqualSets = (isEqual) => (A, B) => {
  const a = normalizeToArray(A);
  const b = normalizeToArray(B);
  if (a.length !== b.length) return false;
  return a.every((av) => b.some((bv) => isEqual(av, bv)));
};

const CustomAutocomplete = ({
  multiple = true,
  label,
  options = [],
  selectedValues = [],
  onApply,                 // parent commits + runs search here
  isLoading = false,
  disabled = false,
  getOptionId,            // optional stable id getter
}) => {


  const isEqual = useMemo(() => makeIsEqual(getOptionId), [getOptionId]);
  const equalSets = useMemo(() => makeEqualSets(isEqual), [isEqual]);

  // committed (from parent)
  const committed = useMemo(
    () => (multiple ? normalizeToArray(selectedValues) : selectedValues ?? null),
    [multiple, selectedValues]
  );

  // draft (what the user edits)
  const [draft, setDraft] = useState(committed);
  const [open, setOpen] = useState(false);
  const [appliedThisSession, setAppliedThisSession] = useState(false);

  // sync draft when parent commits
  useEffect(() => setDraft(committed), [committed]);

  const hasPending = multiple ? !equalSets(draft, committed) : draft !== committed;
  const hasCommitted = multiple
    ? normalizeToArray(committed).length > 0
    : committed != null && committed !== "";

  // Show DRAFT while open; COMMITTED when closed
  const displayedValue = open ? draft : committed;

  const clearAll = () => {
    const empty = multiple ? [] : null;
    setDraft(empty);
    onApply?.(empty);
    setAppliedThisSession(true);
    setOpen(false);
  };

  // footer with Clear / Reset / Apply
  const PaperWithFooter = React.forwardRef(function PaperWithFooter(props, ref) {
    const { children, ...rest } = props;
    return (
      <Paper ref={ref} {...rest}>
        {children}
        {(hasCommitted || hasPending) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              p: 1,
              borderTop: "1px solid rgba(255,255,255,0.3)",
              backgroundColor: "rgb(51, 119, 167)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {hasCommitted && (
                <IconButton
                  size="small"
                  aria-label={`Clear ${label}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearAll}
                  sx={{ color: "white" }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              )}
              {hasPending && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setDraft(committed)}
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{ color: "white", minWidth: 48, height: 28, lineHeight: 1 }}
                >
                  Reset
                </Button>
              )}
            </Box>

            {hasPending && (
              <Button
                variant="outlined"
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const payload = Array.isArray(draft) ? [...draft] : draft ?? null;
                  onApply?.(payload);
                  setAppliedThisSession(true);
                  setOpen(false);
                }}
                sx={{
                  color: "white",
                  borderColor: "white",
                  minWidth: 64,
                  height: 28,
                  lineHeight: 1,
                  px: 1.5,
                  "&:hover": { borderColor: "white" },
                }}
              >
                Apply
              </Button>
            )}
          </Box>
        )}
      </Paper>
    );
  });

  return (
    <Root className="select-dropdown-container-portfolio">
      <Autocomplete
        multiple={multiple}
        disableClearable
        disabled={disabled}
        open={open}
        onOpen={() => {
          setDraft(committed);
          setAppliedThisSession(false);
          setOpen(true);
        }}
        onClose={() => {
          // if user closes without Apply, roll back all selected checkboxes (clears values minus previously applied)
          if (!appliedThisSession) setDraft(committed);
          setOpen(false);
        }}
        PaperComponent={PaperWithFooter}

        // Display draft while open - committed otherwise
        value={displayedValue}

        // Let MUI compute newValue - keep it as draft
        onChange={(_, newValue) => {
          if (isLoading) return;
          if (!open) return;
          setDraft(multiple ? normalizeToArray(newValue) : (newValue ?? null));
        }}

        options={isLoading ? [] : options}
        getOptionLabel={getLabel}
        isOptionEqualToValue={isEqual}
        disableCloseOnSelect
        filterSelectedOptions={false}
        noOptionsText={isLoading ? "Loading..." : "No options"}

        // First chip + "+n" — NO onDelete (chip delete disabled)
        renderTags={(tagValue, getTagProps) => {
          if (!tagValue?.length) return [];
          const chips = [];

          const firstProps = getTagProps({ index: 0 });
          delete firstProps.onDelete;

          chips.push(
            <Chip
              {...firstProps}
              key="first-chip"
              label={getLabel(tagValue[0])}
              size="small"
              sx={{
                color: "white",
                backgroundColor: "transparent",
                border: "1px solid white",
                borderRadius: "5px",
                "& .MuiChip-deleteIcon": { color: "white" },
                mr: 0.5,
                fontSize: "0.75rem",
                height: 24,
                px: 0.5,
                maxWidth: "calc(100% - 64px)",
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          );

          if (tagValue.length > 1) {
            chips.push(
              <Chip
                key="counter"
                label={`+${tagValue.length - 1}`}
                size="small"
                sx={{
                  color: "white",
                  backgroundColor: "transparent",
                  border: "1px solid white",
                  borderRadius: "5px",
                  fontSize: "0.75rem",
                  height: 24,
                  px: 0.5,
                }}
                onDelete={undefined}
              />
            );
          }
          return chips;
        }}

        // Checkboxes reflect the current displayed value (draft while open)
        renderOption={(props, option) => {
          const checked = multiple
            ? normalizeToArray(displayedValue).some((v) => isEqual(v, option))
            : isEqual(displayedValue, option);

          const { key, ...restProps } = props;

          return (
            <li key={key} {...restProps} aria-selected={checked}>
              <Checkbox size="small" checked={checked} sx={{ mr: 1 }} />
              {getLabel(option)}
            </li>
          );
        }}

        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth
            size="small"
            className={`${classes.textField} selector-font-control`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && open && hasPending) {
                e.preventDefault();
                const payload = Array.isArray(draft) ? [...draft] : draft ?? null;
                onApply?.(payload);
                setAppliedThisSession(true);
                setOpen(false);
              }
            }}
          />
        )}

        // Styling
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            flexWrap: "nowrap",
            alignItems: "center",
            minHeight: "36px",
          },
          "& .MuiInputLabel-shrink": {
            maxWidth: "calc(100% - 35px)",
          },
          "& .MuiChip-root": {
            color: "white",
            backgroundColor: "transparent",
            border: "1px solid white",
            borderRadius: "5px",
            maxWidth: "calc(100% - 35px)",
          },
          "& .MuiChip-deleteIcon": { color: "white" },
          "& .MuiAutocomplete-listbox": { maxHeight: 280 },
          "& .MuiAutocomplete-tag": { color: "white" },
        }}
      />
    </Root>
  );
};

export default CustomAutocomplete;
